import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Users, Car, FileText, AlertCircle, Smartphone, TrendingUp } from 'lucide-react';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { driverProfileOf } from '@/lib/driverProfile';
import { businessMonthKey, businessToday } from '@/lib/format';

const CHART_COLORS = ['#404040', '#737373', '#a3a3a3', '#d4d4d4', '#e5e5e5'];

// Custom tooltip for charts
function CustomTooltip({ active, payload }: any) {
    if (!active || !payload?.length) return null;
    const { name, value } = payload[0];
    return (
        <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
            <span className="font-medium">{name}</span>: <span className="text-foreground font-semibold">{value}</span>
        </div>
    );
}

// Custom label for pie chart
function renderCustomLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-xs font-semibold">
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
}

function StatCard({
    title, value, subtitle, icon: Icon, trend,
}: {
    title: string; value: number | string; subtitle: string;
    icon: any; trend?: string;
}) {
    return (
        <Card className="border border-border/40">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
                <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-muted">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-3xl font-bold tracking-tight">{value}</div>
                <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
                {trend && <p className="text-xs text-muted-foreground font-medium mt-1">{trend}</p>}
            </CardContent>
        </Card>
    );
}

function ProfileField({ label, value, total }: { label: string; value: number; total: number }) {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium">{pct}%<span className="text-muted-foreground font-normal ml-1">({value}/{total})</span></span>
            </div>
            <Progress value={pct} className="h-2" />
        </div>
    );
}

export default function DriversAnalytics() {
    const { data, isLoading } = useQuery({
        queryKey: ['drivers-analytics-v2'],
        queryFn: async () => {
            // ── Fetch all drivers with profiles ──
            const { data: drivers, error: dErr } = await supabase
                .from('drivers')
                .select('id, created_at, profiles:profiles!profiles_driver_id_fkey(id, admin_id, phone, email, address, birth_date, photo, user_id)');
            if (dErr) throw dErr;

            // ── Fetch all contracts with latest status ──
            const { data: contracts, error: cErr } = await supabase
                .from('contracts')
                .select('driver_id, created_at, contract_updates(status, created_at)');
            if (cErr) throw cErr;

            const totalDrivers = drivers?.length || 0;

            // ── Contract status map ──
            const driverStatusMap = new Map<number, string>();
            contracts?.forEach(c => {
                if (!driverStatusMap.has(c.driver_id)) {
                    const updates = c.contract_updates || [];
                    if (updates.length > 0) {
                        const sorted = [...updates].sort((a, b) =>
                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                        );
                        driverStatusMap.set(c.driver_id, sorted[0].status);
                    }
                }
            });

            const statusCounts = { IN_PROGRESS: 0, COMPLETED: 0, TERMINATED: 0, DRAFT: 0, NO_CONTRACT: 0 };
            drivers?.forEach(d => {
                const status = driverStatusMap.get(d.id);
                if (status && status in statusCounts) {
                    statusCounts[status as keyof typeof statusCounts]++;
                } else {
                    statusCounts.NO_CONTRACT++;
                }
            });

            // ── Profile completeness ──
            let withPhone = 0, withEmail = 0, withAddress = 0, withDob = 0, withPhoto = 0, withAuth = 0;
            drivers?.forEach(d => {
                // The driver's own row: an admin's complete test profile against a driver id would
                // otherwise be counted as that driver's phone, email, address and date of birth.
                const p = driverProfileOf(d.profiles);
                if (!p) return;
                if (p.phone) withPhone++;
                if (p.email) withEmail++;
                if (p.address) withAddress++;
                if (p.birth_date) withDob++;
                if (p.photo) withPhoto++;
                if (p.user_id) withAuth++;
            });

            // ── Monthly registrations (last 12 months) ──
            const monthMap = new Map<string, number>();
            const now = businessToday();
            for (let i = 11; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                monthMap.set(key, 0);
            }
            drivers?.forEach(d => {
                // The stored Ghana month. `created_at` is a timestamptz, so `new Date(…)` read
                // through local getters moves every registration within the viewer's offset of a
                // month boundary into the neighbouring bar.
                const key = businessMonthKey(d.created_at);
                if (key && monthMap.has(key)) {
                    monthMap.set(key, (monthMap.get(key) || 0) + 1);
                }
            });
            const registrationTrend = Array.from(monthMap.entries()).map(([month, count]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
                count,
            }));

            // ── This month vs last month ──
            const thisMonth = registrationTrend[registrationTrend.length - 1]?.count || 0;
            const lastMonth = registrationTrend[registrationTrend.length - 2]?.count || 0;

            // ── Contracts per driver ──
            const contractCountMap = new Map<number, number>();
            contracts?.forEach(c => {
                contractCountMap.set(c.driver_id, (contractCountMap.get(c.driver_id) || 0) + 1);
            });
            let singleContract = 0, multiContract = 0;
            contractCountMap.forEach(count => {
                if (count === 1) singleContract++;
                else if (count > 1) multiContract++;
            });

            // ── Retention rate (drivers who completed a contract and started another) ──
            const completedDrivers = new Set<number>();
            const activeAfterComplete = new Set<number>();
            contracts?.forEach(c => {
                const updates = c.contract_updates || [];
                const sorted = [...updates].sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                if (sorted.length > 0) {
                    if (sorted[0].status === 'COMPLETED') completedDrivers.add(c.driver_id);
                }
            });
            // Drivers who completed AND have another active contract
            contracts?.forEach(c => {
                if (completedDrivers.has(c.driver_id)) {
                    const updates = c.contract_updates || [];
                    const sorted = [...updates].sort((a, b) =>
                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    );
                    if (sorted.length > 0 && sorted[0].status === 'IN_PROGRESS') {
                        activeAfterComplete.add(c.driver_id);
                    }
                }
            });

            return {
                totalDrivers,
                statusCounts,
                activeDrivers: statusCounts.IN_PROGRESS,
                driversWithContract: totalDrivers - statusCounts.NO_CONTRACT,
                driversWithoutContract: statusCounts.NO_CONTRACT,
                profileComplete: { total: totalDrivers, withPhone, withEmail, withAddress, withDob, withPhoto, withAuth },
                registrationTrend,
                thisMonth,
                lastMonth,
                retentionCount: activeAfterComplete.size,
                completedCount: completedDrivers.size,
                singleContract,
                multiContract,
                statusData: [
                    { name: 'Active', value: statusCounts.IN_PROGRESS },
                    { name: 'Completed', value: statusCounts.COMPLETED },
                    { name: 'Terminated', value: statusCounts.TERMINATED },
                    { name: 'No Contract', value: statusCounts.NO_CONTRACT },
                    { name: 'Draft', value: statusCounts.DRAFT },
                ].filter(d => d.value > 0),
            };
        },
    });

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
                <div className="grid gap-6 lg:grid-cols-2">
                    <Skeleton className="h-80" />
                    <Skeleton className="h-80" />
                </div>
            </div>
        );
    }

    const activeRate = data && data.totalDrivers > 0
        ? Math.round((data.activeDrivers / data.totalDrivers) * 100) : 0;
    const retentionRate = data && data.completedCount > 0
        ? Math.round((data.retentionCount / data.completedCount) * 100) : 0;

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title="Total Drivers"
                    value={data?.totalDrivers || 0}
                    subtitle="Registered in the system"
                    icon={Users}
                    trend={data && data.thisMonth > 0 ? `+${data.thisMonth} this month` : undefined}
                />
                <StatCard
                    title="Active Drivers"
                    value={data?.activeDrivers || 0}
                    subtitle={`${activeRate}% of fleet`}
                    icon={Car}
                />
                <StatCard
                    title="App Linked"
                    value={data?.profileComplete.withAuth || 0}
                    subtitle={`${data ? Math.round((data.profileComplete.withAuth / data.totalDrivers) * 100) : 0}% using the app`}
                    icon={Smartphone}
                />
                <StatCard
                    title="Retention Rate"
                    value={`${retentionRate}%`}
                    subtitle={`${data?.retentionCount || 0} of ${data?.completedCount || 0} re-contracted`}
                    icon={TrendingUp}
                />
            </div>

            {/* Charts Row */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Contract Status Distribution */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle className="text-base">Contract Status</CardTitle>
                        <CardDescription>Breakdown by latest contract status</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center gap-6">
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie
                                        data={data?.statusData || []}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={55}
                                        outerRadius={95}
                                        paddingAngle={3}
                                        dataKey="value"
                                        labelLine={false}
                                        label={renderCustomLabel}
                                    >
                                        {(data?.statusData || []).map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        {/* Legend */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2 justify-center">
                            {(data?.statusData || []).map((entry, i) => (
                                <div key={entry.name} className="flex items-center gap-1.5 text-xs">
                                    <div className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i] }} />
                                    <span className="text-muted-foreground">{entry.name}</span>
                                    <span className="font-semibold">{entry.value}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Registration Trend */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle className="text-base">Registration Trend</CardTitle>
                        <CardDescription>New driver registrations per month (last 12 months)</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={data?.registrationTrend || []} barSize={24}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                                <XAxis
                                    dataKey="month"
                                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={30}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" name="Registrations" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Bottom Row */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Profile Completeness */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle className="text-base">Profile Completeness</CardTitle>
                        <CardDescription>How complete are driver profiles</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <ProfileField label="Phone Number" value={data?.profileComplete.withPhone || 0} total={data?.totalDrivers || 1} />
                        <ProfileField label="Photo" value={data?.profileComplete.withPhoto || 0} total={data?.totalDrivers || 1} />
                        <ProfileField label="Address" value={data?.profileComplete.withAddress || 0} total={data?.totalDrivers || 1} />
                        <ProfileField label="Date of Birth" value={data?.profileComplete.withDob || 0} total={data?.totalDrivers || 1} />
                        <ProfileField label="Email" value={data?.profileComplete.withEmail || 0} total={data?.totalDrivers || 1} />
                        <ProfileField label="App Account" value={data?.profileComplete.withAuth || 0} total={data?.totalDrivers || 1} />
                    </CardContent>
                </Card>

                {/* Fleet Overview */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle className="text-base">Fleet Overview</CardTitle>
                        <CardDescription>Contract assignment summary</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-xl border p-4">
                                    <p className="text-2xl font-bold">{data?.statusCounts.IN_PROGRESS || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Active contracts</p>
                                </div>
                                <div className="rounded-xl border p-4">
                                    <p className="text-2xl font-bold">{data?.statusCounts.COMPLETED || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Completed</p>
                                </div>
                                <div className="rounded-xl border p-4">
                                    <p className="text-2xl font-bold">{data?.statusCounts.TERMINATED || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Terminated</p>
                                </div>
                                <div className="rounded-xl border p-4">
                                    <p className="text-2xl font-bold">{data?.driversWithoutContract || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Unassigned</p>
                                </div>
                            </div>

                            <div className="pt-3 border-t space-y-3">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">Single contract drivers</span>
                                    <span className="font-medium">{data?.singleContract || 0}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">Multi-contract drivers</span>
                                    <span className="font-medium">{data?.multiContract || 0}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">Utilisation rate</span>
                                    <span className="font-semibold">{activeRate}%</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
